import { DecimalPipe, DOCUMENT, NgClass } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  OnInit,
  signal,
  ViewEncapsulation,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslatePipe } from "@ngx-translate/core";
import { nanoid } from "nanoid";
import { filter, finalize, map, Observable, Subject, take, takeUntil, timer } from "rxjs";
import { Queue } from "../../class/queue";
import { ToastType } from "../../enums/toast-type.enum";
import { IconRhombWarningComponent } from "../../icons/icon-rhomb-warning.component";
import { IconTriangleWarningComponent } from "../../icons/icon-triangle-warning.component";
import { ToastConfig } from "../../models/toast-config.model";
import { ToastService } from "../../services";
import { AppToast } from "./app-toast.model";

/**
 * Follows Siemens's style guidelines for toasts
 * https://www.figma.com/file/3vNWcuevwej4Fs6ec7LOlE/2.02-Product-UI-%E2%80%93-Desktop?node-id=556%3A5167&mode=dev
 */
@Component({
  selector: "app-toasts",
  templateUrl: "./toasts.component.html",
  styleUrls: ["./toasts.component.scss"],
  imports: [NgClass, TranslatePipe, IconTriangleWarningComponent, IconRhombWarningComponent, DecimalPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Disable view encapsulation to allow animations to work properly
  encapsulation: ViewEncapsulation.None,
  host: {
    /* eslint-disable @typescript-eslint/naming-convention */
    "(document:visibilitychange)": "onDocumentVisibilityChange()",
    /* eslint-enable @typescript-eslint/naming-convention */
  },
})
export class ToastsComponent implements OnInit, OnDestroy {
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly toastService = inject(ToastService);
  protected readonly document = inject(DOCUMENT, { optional: true }) ?? window.document;
  protected readonly ngZone = inject(NgZone);
  protected readonly changeDetectorRef = inject(ChangeDetectorRef);
  protected readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  public readonly closeButtonAriaLabel = input<string>("Close");

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public readonly ToastType = ToastType;

  public readonly toastList = signal<readonly AppToast[]>([]);

  private readonly pendingToastQueue = new Queue<AppToast>();

  /** Time when view was suspended */
  private hiddenDateTime: number = 0;

  public readonly enterAnimationTime: number = 225;
  public readonly leaveAnimationTime: number = 225;

  private readonly toastClosedSubject = new Subject<string>();

  public onDocumentVisibilityChange(): void {
    this.changeDetectorRef.markForCheck();
    // Update timers displays for the user
    if (this.toastList().length > 0) {
      if (this.document.hidden) {
        this.hiddenDateTime = performance.now();
        return;
      }
      if (this.hiddenDateTime > 0) {
        const timeHidden = performance.now() - this.hiddenDateTime;
        this.toastList.update((list) =>
          list.map((toast) => {
            if (toast) toast._timeRemaining = Math.max((toast._timeRemaining ?? 0) - timeHidden, 0);
            return toast;
          })
        );
      }
    }
  }

  public ngOnInit(): void {
    const unregisterFn = this.toastService.registerNewToastConfigFn((toastConfig) => toastConfig && this.fireToast(toastConfig));
    this.destroyRef.onDestroy(() => unregisterFn());
  }

  public ngOnDestroy(): void {
    this.toastClosedSubject.complete();
  }

  public closeToast(id: string): void {
    this.toastList.update((list) =>
      list.reduce((result, next) => {
        if (next.id !== id) result.push(next);
        return result;
      }, new Array<AppToast>())
    );
  }

  public onAnimationEnd(element: HTMLElement, event: AnimationEvent, toast: AppToast): void {
    this.changeDetectorRef.markForCheck();
    if (event.target !== element || !(event instanceof AnimationEvent)) return;
    const animationName = event.animationName ?? "";
    if (animationName.endsWith("app-toast-enter") || animationName.endsWith("app-toast-enter-fallback")) {
      this.toastEnterDone(toast);
    } else if (animationName.endsWith("app-toast-leave") || animationName.endsWith("app-toast-leave-fallback")) {
      this.toastLeaveDone(toast);
    }
  }

  private toastEnterDone(toast: AppToast): void {
    if (!toast._ready) {
      toast._ready = true;
      this.checkPendingToasts();
    }
  }

  private toastLeaveDone(toast: AppToast): void {
    if (!this.toastList().includes(toast)) {
      toast._removed = true;
      this.toastClosedSubject.next(toast.id);
    }
  }

  private fireToast(toastConfig: ToastConfig): void {
    this.changeDetectorRef.markForCheck();
    toastConfig = Object.assign({}, toastConfig ?? {});
    toastConfig.title = toastConfig.title?.toString();
    toastConfig.text = toastConfig.text?.toString();

    if (toastConfig.timer === Number.POSITIVE_INFINITY || toastConfig.timer === Infinity) {
      toastConfig.timer = 0;
    }

    // ensure timer is set if there's no close button
    if (!toastConfig.showCloseButton && (!toastConfig.timer || toastConfig.timer < 300)) {
      toastConfig.timer = 3500;
    }

    const toast: AppToast = Object.assign(toastConfig, {
      id: `id_${nanoid()}`,
      titleTranslateParams: toastConfig.titleTranslateParams,
      type: toastConfig.type ?? ToastType.Info,
      textTranslateParams: toastConfig.textTranslateParams,
      displayTimer: toastConfig.timer <= 0 ? false : toastConfig.timerProgressBar,
    });

    this.pendingToastQueue.enqueue(toast);
    this.checkPendingToasts();
  }

  /** Check if there are pending toasts and show the next one if the displayed ones are all ready */
  private checkPendingToasts(): void {
    const toastList = this.toastList();
    if (this.pendingToastQueue.size > 0 && (toastList.length < 1 || toastList.every((x) => x._ready))) {
      const toast = this.pendingToastQueue.dequeue();
      if (!toast) {
        this.checkPendingToasts();
        return;
      }

      toastList.forEach((other) => {
        // close toasts with the same text / title
        if (other && other.title === toast.title && other.text === toast.text) {
          this.ngZone.runOutsideAngular(() =>
            timer(this.enterAnimationTime)
              .pipe(take(1), takeUntil(this.getToastClosedObservable(other.id)), takeUntilDestroyed(this.destroyRef))
              .subscribe(() =>
                this.ngZone.run(() => {
                  this.changeDetectorRef.markForCheck();
                  this.closeToast(other.id);
                })
              )
          );
        }
      });

      const newList = [toast];
      toastList.forEach((toast1) => newList.push(toast1));
      this.toastList.set(newList);

      if (toast.timer !== null && toast.timer !== undefined && typeof toast.timer === "number" && toast.timer > 0) {
        toast._timeRemaining = toast.timer + this.enterAnimationTime;
        toast._timeRemainingPercent = 100;
        this.startUserDisplayTimer(toast);
      }
    }
  }

  private getToastClosedObservable(id: string): Observable<void> {
    return this.toastClosedSubject.pipe(
      filter((x) => x === id),
      map(() => <void>undefined)
    );
  }

  /* start the timer displayed to the user */
  private startUserDisplayTimer(toast: AppToast): void {
    if ((toast._timeRemaining ?? 0) > 0) {
      const stopTimer = new Subject<void>();
      const tInterval = 50;
      this.ngZone.runOutsideAngular(() =>
        timer(0, tInterval)
          .pipe(
            takeUntil(this.getToastClosedObservable(toast.id)),
            takeUntil(stopTimer),
            takeUntilDestroyed(this.destroyRef),
            finalize(() => stopTimer.complete())
          )
          .subscribe(() =>
            this.ngZone.run(() => {
              this.changeDetectorRef.markForCheck();
              if ((toast._timeRemaining ?? 0) <= 0) {
                stopTimer.next();
                this.closeToast(toast.id);
                return;
              }
              if (!toast._hovered || !toast.showCloseButton || !toast.displayTimer) {
                toast._timeRemaining = Math.max((toast._timeRemaining ?? 0) - tInterval, 0);
                toast._timeRemainingPercent = (toast._timeRemaining / toast.timer) * 100;
              }
            })
          )
      );
    }
  }
}
